import type { ReactNode } from 'react';
import { Unplug } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { t } from '@/i18n';
import type { FinancialOverview as FinancialOverviewData } from '@ip/shared-types';
import { AgingChart } from './AgingChart';
import { CashFlowChart } from './CashFlowChart';
import { DashboardSection } from './DashboardSection';
import { FinancialKpiTiles } from './FinancialKpiTiles';
import { FxRatesCard } from './FxRatesCard';
import { MarginChart } from './MarginChart';
import { RevenueCostChart } from './RevenueCostChart';

function NoSalesCard() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex items-center gap-3 py-6">
        <Unplug aria-hidden="true" className="text-muted-foreground size-8 shrink-0 opacity-60" />
        <p className="text-muted-foreground text-sm">{t('dashboard.financial.noSales')}</p>
      </CardContent>
    </Card>
  );
}

/**
 * "Kondycja spółki" — the lead band: hero KPI row, then revenue-vs-cost and
 * margin across the year, cash flow, and receivables/payables aging.
 * `costDetails` (the folded cost-side charts) is slotted in at the end.
 */
export function HealthSection({
  data,
  costDetails,
}: {
  data: FinancialOverviewData;
  costDetails?: ReactNode;
}) {
  const hasSales = data.kpis.revenueYtd !== 0 || data.months.some((m) => m.revenueNet !== 0);
  const showReceivables = hasSales || data.kpis.receivablesTotal !== 0;

  return (
    <DashboardSection
      title={t('dashboard.health.section')}
      subtitle={t('dashboard.health.sectionSubtitle')}
    >
      <FinancialKpiTiles
        kpis={data.kpis}
        receivables={data.receivables}
        payables={data.payables}
        hasSales={hasSales}
      />

      {hasSales ? (
        <>
          <RevenueCostChart months={data.months} year={data.year} />
          <div className="grid gap-4 xl:grid-cols-2">
            <MarginChart months={data.months} year={data.year} />
            <CashFlowChart months={data.months} year={data.year} />
          </div>
        </>
      ) : (
        <>
          <NoSalesCard />
          <CashFlowChart months={data.months} year={data.year} />
        </>
      )}

      <AgingChart
        receivables={data.receivables}
        payables={data.payables}
        showReceivables={showReceivables}
      />

      <FxRatesCard />

      {costDetails}
    </DashboardSection>
  );
}
